/**
 * Client API REST — point d'entrée unique pour les appels backend synolog.
 * Standards Synotis : JWT stocké dans localStorage['hg_token'].
 */

import { IS_NATIVE, NATIVE_API_ORIGIN } from '../native/platform.js';

// Web : chemin relatif (même origine, reverse proxy). Natif : la WebView sert
// l'app depuis `capacitor://localhost` ⇒ origine absolue de l'API
// (`VITE_API_BASE_URL`). En build web `NATIVE_API_ORIGIN` vaut '' ⇒ '/api'.
const API_URL = `${NATIVE_API_ORIGIN}/api`;
const TOKEN_KEY = 'hg_token';
// Impersonation : pendant qu'un admin agit "en tant que" un autre user, son
// token admin réel est mis de côté ici pour pouvoir y revenir.
const ADMIN_TOKEN_KEY = 'hg_token_admin';
// Flag explicite d'impersonation. INDISPENSABLE en mode SSO (Lot C) : l'admin
// est authentifié par le cookie `syn_session` et n'a AUCUN token local — on ne
// peut donc pas déduire l'état d'impersonation de la seule présence d'un token
// admin sauvegardé. Sans ce flag, `endImpersonation` ne purgeait jamais le token
// d'impersonation et l'admin restait coincé en « tant que » (bug Sprint 18 / SSO).
const IMPERSONATING_KEY = 'hg_impersonating';

/* ------------------------------------------------------------------------ *
 * Sprint App native — Lot 3. Session de la coquille Capacitor.
 *
 * Contrat d'auth mobile §8 : l'access token (JWT synocore, aud
 * `synolog-mobile`, 15 min) vit EN MÉMOIRE uniquement ; le refresh token est
 * en Keychain/Keystore, manipulé par `native/nativeAuth.js` qui s'enregistre
 * ici via `configureNativeAuth`. Pas d'import Capacitor dans ce fichier : le
 * bundle web n'embarque rien de natif.
 * ------------------------------------------------------------------------ */
let nativeAccessToken = null;
// Échéance LOCALE de l'access token natif (ms, horloge de l'appareil) :
// réception + durée de vie (`exp - iat`), insensible au décalage d'horloge.
let nativeTokenExpiresAt = null;
let nativeTokenReceivedAt = 0;
let nativeHooks = { refresh: null, onAuthLost: null };
// Verrou anti-rafale : tous les 401 simultanés partagent UN seul refresh.
let nativeRefreshPromise = null;

/** Événement émis quand la session native est perdue (refresh impossible). */
export const NATIVE_AUTH_LOST_EVENT = 'synolog:native-auth-lost';
/** Événement émis à chaque nouvel access token natif (login ou refresh). */
export const NATIVE_TOKEN_EVENT = 'synolog:native-token';
/** Événement émis au retour de l'app au premier plan (cf. nativeBootstrap). */
export const NATIVE_RESUME_EVENT = 'synolog:native-resume';
/**
 * #94 — Événement émis après une saisie réussie qui change « Mon mois »
 * (imputations, planning, statuts) : le widget iOS se met à jour.
 */
export const NATIVE_DATA_CHANGED_EVENT = 'synolog:native-data-changed';
const MONTH_DATA_PATH = /^\/(logged-time|allocations|work-locations)([/?]|$)/;
/** Message lisible pour un refus de périmètre mobile (403 `mobile_scope`). */
export const MOBILE_SCOPE_MESSAGE = "Cette action n'est pas disponible dans l'application mobile.";

/**
 * Branche le module d'auth natif : `refresh()` doit renouveler l'access token
 * (via `setToken`) et résoudre `true`/`false` ; `onAuthLost()` purge le
 * stockage sécurisé.
 */
export function configureNativeAuth(hooks) {
  nativeHooks = { ...nativeHooks, ...hooks };
}

/**
 * Garantit un access token plus récent que `usedToken` (celui qui vient de
 * prendre un 401). Si un autre appel l'a déjà renouvelé, on rejoue sans
 * refaire de refresh ; sinon un seul refresh partagé.
 */
async function ensureFreshNativeToken(usedToken) {
  if (nativeAccessToken && nativeAccessToken !== usedToken) return true;
  if (!nativeHooks.refresh) return false;
  if (!nativeRefreshPromise) {
    nativeRefreshPromise = Promise.resolve()
      .then(() => nativeHooks.refresh())
      .then(Boolean)
      .catch(() => false)
      .finally(() => {
        nativeRefreshPromise = null;
      });
  }
  return nativeRefreshPromise;
}

/** Échéance locale (ms) d'un JWT reçu maintenant, ou `null` si illisible. */
function computeNativeTokenExpiry(token) {
  try {
    const part = token.split('.')[1];
    const b64 = part.replace(/-/g, '+').replace(/_/g, '/');
    const claims = JSON.parse(atob(b64.padEnd(Math.ceil(b64.length / 4) * 4, '=')));
    if (typeof claims.exp !== 'number') return null;
    const lifetimeMs =
      typeof claims.iat === 'number' ? (claims.exp - claims.iat) * 1000 : claims.exp * 1000 - Date.now();
    return Date.now() + lifetimeMs;
  } catch {
    return null;
  }
}

/**
 * Vrai si l'access token natif expire dans moins de `marginMs` (ou est
 * absent). Un jeton reçu il y a moins de 5 s n'est jamais jugé « proche de
 * l'expiration » : garde-fou anti-boucle de refresh (horloge, TTL très court).
 */
function nativeTokenExpiresSoon(marginMs) {
  if (!nativeAccessToken) return true;
  if (Date.now() - nativeTokenReceivedAt < 5000) return false;
  if (nativeTokenExpiresAt == null) return false; // illisible : on laisse le 401 décider
  return nativeTokenExpiresAt - Date.now() < marginMs;
}

/** Session native irrécupérable : purge + retour à l'écran de connexion. */
function notifyNativeAuthLost() {
  nativeAccessToken = null;
  try {
    nativeHooks.onAuthLost?.();
  } catch {
    // best-effort
  }
  window.dispatchEvent(new CustomEvent(NATIVE_AUTH_LOST_EVENT));
}

export function getToken() {
  if (IS_NATIVE) return nativeAccessToken;
  return localStorage.getItem(TOKEN_KEY);
}

export function setToken(token) {
  if (IS_NATIVE) {
    const changed = (token || null) !== nativeAccessToken;
    nativeAccessToken = token || null;
    if (changed) {
      nativeTokenReceivedAt = Date.now();
      nativeTokenExpiresAt = nativeAccessToken ? computeNativeTokenExpiry(nativeAccessToken) : null;
    }
    // Le flux SSE n'est authentifié qu'à son ouverture : on le rouvre à chaque
    // nouveau jeton (cf. openEventStream).
    if (changed && nativeAccessToken) window.dispatchEvent(new CustomEvent(NATIVE_TOKEN_EVENT));
    return;
  }
  if (token) localStorage.setItem(TOKEN_KEY, token);
  else localStorage.removeItem(TOKEN_KEY);
}

export function clearToken() {
  if (IS_NATIVE) {
    nativeAccessToken = null;
    return;
  }
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(ADMIN_TOKEN_KEY);
  localStorage.removeItem(IMPERSONATING_KEY);
}

/** True si une impersonation est en cours (flag explicite). */
export function isImpersonating() {
  // Impersonation désactivée dans l'app native (contrat §6 : voie mobile sans
  // impersonation côté API).
  if (IS_NATIVE) return false;
  return localStorage.getItem(IMPERSONATING_KEY) === '1';
}

/**
 * Bascule sur le token d'impersonation. En session legacy (token admin local),
 * ce token est mis de côté pour pouvoir y revenir. En session SSO, il n'y a pas
 * de token local : le retour se fera en purgeant le token d'impersonation, ce
 * qui laisse le cookie `syn_session` (admin réel) reprendre la main. Le flag
 * `IMPERSONATING_KEY` trace l'état dans les deux cas (pas d'imbrication).
 */
export function beginImpersonation(impersonationToken) {
  if (IS_NATIVE) return; // cf. isImpersonating
  if (isImpersonating()) return; // pas d'imbrication
  const current = getToken();
  if (current) {
    localStorage.setItem(ADMIN_TOKEN_KEY, current);
  }
  localStorage.setItem(IMPERSONATING_KEY, '1');
  setToken(impersonationToken);
}

/**
 * Revient au compte admin réel. No-op si aucune impersonation en cours.
 * - Session legacy : restaure le token admin sauvegardé.
 * - Session SSO : purge le token d'impersonation → l'auth retombe sur le cookie
 *   `syn_session` (admin réel), que le backend ignore tant qu'un Bearer existe.
 */
export function endImpersonation() {
  if (!isImpersonating()) return;
  const admin = localStorage.getItem(ADMIN_TOKEN_KEY);
  if (admin) {
    localStorage.setItem(TOKEN_KEY, admin);
  } else {
    localStorage.removeItem(TOKEN_KEY);
  }
  localStorage.removeItem(ADMIN_TOKEN_KEY);
  localStorage.removeItem(IMPERSONATING_KEY);
}

/**
 * Helper bas niveau : préfixe /api, injecte le JWT, parse le JSON, throw sur !ok.
 */
/**
 * Lit la valeur d'un cookie lisible (non-HttpOnly) côté navigateur.
 * Utilisé pour le token CSRF SSO (`syn_csrf`) posé par synotis-core.
 */
function readCookie(name) {
  const m = document.cookie.match(
    new RegExp('(?:^|; )' + name.replace(/[-.]/g, '\\$&') + '=([^;]*)'),
  );
  return m ? decodeURIComponent(m[1]) : null;
}

// Nom du cookie CSRF posé par synotis-core — DÉPEND de l'environnement
// (prod: `syn_csrf` ; staging: `syn_csrf_stg`). Doit correspondre à
// `CSRF_COOKIE_NAME` côté API. Injecté au build via VITE_CSRF_COOKIE_NAME ;
// défaut prod si absent. (Sinon le front renvoie le mauvais token et le
// double-submit échoue avec « Jeton CSRF invalide » sur toutes les mutations.)
const CSRF_COOKIE_NAME = import.meta.env.VITE_CSRF_COOKIE_NAME || 'syn_csrf';
const MUTATION_METHODS = new Set(['POST', 'PATCH', 'PUT', 'DELETE']);

/**
 * Variante native d'`apiFetch` : Bearer = access token en mémoire, pas de
 * cookie (`credentials: 'omit'` ⇒ aucune dépendance au CORS avec
 * credentials), pas de CSRF (voie Bearer exemptée côté API). Sur 401 : un
 * refresh partagé puis UN rejeu ; échec ⇒ purge + écran de connexion.
 */
async function nativeApiFetch(path, options = {}, isRetry = false) {
  const headers = { ...(options.headers || {}) };
  const token = nativeAccessToken;
  if (token) headers.Authorization = `Bearer ${token}`;
  if (options.body && !headers['Content-Type']) {
    headers['Content-Type'] = 'application/json';
  }
  const res = await fetch(`${API_URL}${path}`, {
    ...options,
    credentials: 'omit',
    headers,
  });

  if (res.status === 401) {
    if (!isRetry && (await ensureFreshNativeToken(token))) {
      return nativeApiFetch(path, options, true);
    }
    notifyNativeAuthLost();
    const body = await res.json().catch(() => ({}));
    const err = new Error(body.error || 'Non authentifié');
    err.status = 401;
    throw err;
  }
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    // Contrat §7 bis — route hors liste blanche mobile : message lisible.
    const isScope = res.status === 403 && body.error === 'mobile_scope';
    const err = new Error(
      isScope ? MOBILE_SCOPE_MESSAGE : body.error || res.statusText || `HTTP ${res.status}`,
    );
    err.status = res.status;
    if (isScope) err.code = 'mobile_scope';
    throw err;
  }
  const method = (options.method || 'GET').toUpperCase();
  if (MUTATION_METHODS.has(method) && MONTH_DATA_PATH.test(path)) {
    window.dispatchEvent(new CustomEvent(NATIVE_DATA_CHANGED_EVENT));
  }
  if (res.status === 204) return null;
  return res.json();
}

export async function apiFetch(path, options = {}) {
  if (IS_NATIVE) return nativeApiFetch(path, options);
  const headers = { ...(options.headers || {}) };
  // Lot C — la session normale passe par le cookie SSO (`credentials:include`).
  // On n'envoie le Bearer QUE si un token local existe : voie legacy +
  // impersonation Sprint 18 (token admin/cible stocké en localStorage).
  const token = getToken();
  if (token) headers.Authorization = `Bearer ${token}`;
  if (options.body && !headers['Content-Type']) {
    headers['Content-Type'] = 'application/json';
  }
  // Lot C — CSRF double-submit : sur les mutations, renvoie le token du cookie
  // `syn_csrf` en en-tête `X-CSRF-Token`. (L'enforcement serveur est Lot D ;
  // ici on prépare juste l'émission.)
  const method = (options.method || 'GET').toUpperCase();
  if (MUTATION_METHODS.has(method) && !headers['X-CSRF-Token']) {
    const csrf = readCookie(CSRF_COOKIE_NAME);
    if (csrf) headers['X-CSRF-Token'] = csrf;
  }

  const res = await fetch(`${API_URL}${path}`, {
    ...options,
    credentials: 'include',
    headers,
  });

  if (res.status === 401) {
    // F-S18-02 — Si une impersonation est en cours, un 401 = token
    // d'impersonation expiré/invalide. On NE purge PAS le token admin réel
    // (sinon lockout : l'admin perd sa propre session). On le restaure et on
    // recharge pour revenir proprement au compte admin.
    if (isImpersonating()) {
      endImpersonation();
      window.location.reload();
    } else {
      setToken(null);
    }
    const body = await res.json().catch(() => ({}));
    throw new Error(body.error || 'Non authentifié');
  }
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    const err = new Error(body.error || res.statusText || `HTTP ${res.status}`);
    // Expose le code HTTP pour permettre aux appelants de discriminer (ex. 423
    // verrou comptable côté Logtime mobile).
    err.status = res.status;
    throw err;
  }
  if (res.status === 204) return null;
  return res.json();
}

/**
 * Sprint 17 — Client SSE basé sur `fetch` + `ReadableStream`.
 *
 * Pourquoi pas l'`EventSource` natif : il ne sait pas envoyer de header
 * `Authorization`, et on refuse le fallback `?token=` en query (F-AUTH-01).
 * On lit donc le flux manuellement et on transmet le Bearer JWT.
 *
 * Gère la reconnexion automatique avec backoff exponentiel (1s → 15s max),
 * remis à zéro seulement après une connexion restée ouverte au moins 30 s
 * (un serveur qui accepte puis coupe aussitôt ne provoque pas de rafale).
 * Une réponse 401 ne déclenche PAS de reconnexion (le token est invalide →
 * on laisse l'app rediriger au login).
 *
 * Natif (F-MOB-07) : l'API ferme le flux à l'expiration de l'access token.
 * La reconnexion qui suit renouvelle d'abord le jeton s'il est expiré ou
 * proche de l'être, via le verrou partagé `ensureFreshNativeToken` (jamais
 * de refresh concurrent) ; un refresh en échec réseau ⇒ nouvel essai au
 * rythme du backoff, sans perdre la session.
 *
 * @param {string} path  Chemin relatif sous /api (ex: '/schedule/stream').
 * @param {{ onEvent?: (e:{event:string,data:string})=>void,
 *           onOpen?: ()=>void, onError?: (err:Error)=>void }} handlers
 * @returns {{ close: () => void }}
 */
export function openEventStream(path, { onEvent, onOpen, onError } = {}) {
  let closed = false;
  let controller = null;
  let retryMs = 1000;
  const MAX_RETRY_MS = 15000;
  // Durée minimale d'une connexion pour remettre le backoff à zéro.
  const STABLE_CONNECTION_MS = 30000;
  // Natif : marge avant expiration sous laquelle on renouvelle le jeton avant
  // d'ouvrir le flux (sinon le serveur le refermerait aussitôt).
  const NATIVE_REFRESH_MARGIN_MS = 30000;
  // Natif : un seul refresh tenté par 401 (remis à zéro à chaque ouverture).
  let nativeRefreshTried = false;
  // Natif : l'API ne vérifie le Bearer qu'à l'ouverture du flux ⇒ on le
  // rouvre à chaque nouveau jeton et au retour au premier plan (un flux resté
  // ouvert survivrait sinon à l'expiration de l'access token).
  let reconnectTimer = null;
  // Génération de connexion : une connexion supplantée (réouverture) ne doit
  // ni se relancer elle-même ni remonter d'erreur.
  let generation = 0;
  function reopenNow() {
    if (closed) return;
    clearTimeout(reconnectTimer);
    controller?.abort();
    retryMs = 1000;
    // `nativeRefreshTried` n'est PAS remis à zéro ici (seulement à l'ouverture
    // réussie) : un 401 persistant avec un jeton neuf ne boucle pas.
    connect();
  }
  if (IS_NATIVE) {
    window.addEventListener(NATIVE_TOKEN_EVENT, reopenNow);
    window.addEventListener(NATIVE_RESUME_EVENT, reopenNow);
  }

  function parseSseBlock(raw) {
    let event = 'message';
    const dataLines = [];
    for (const line of raw.split('\n')) {
      if (line.startsWith(':')) continue; // commentaire / heartbeat
      if (line.startsWith('event:')) event = line.slice(6).trim();
      else if (line.startsWith('data:')) dataLines.push(line.slice(5).replace(/^ /, ''));
    }
    if (dataLines.length === 0) return null;
    return { event, data: dataLines.join('\n') };
  }

  async function connect() {
    if (closed) return;
    generation += 1;
    const myGeneration = generation;
    const ctrl = new AbortController();
    controller = ctrl;
    let openedAt = 0;
    if (IS_NATIVE && nativeTokenExpiresSoon(NATIVE_REFRESH_MARGIN_MS)) {
      // Refresh préalable, partagé avec les appels API (verrou unique). Un
      // nouveau jeton déclenche `reopenNow` (génération changée) : cette
      // tentative s'efface alors au profit de la réouverture.
      const refreshed = await ensureFreshNativeToken(getToken());
      if (closed || myGeneration !== generation) return;
      if (!refreshed) {
        if (!getToken()) {
          // Refresh refusé (session révoquée, p. ex. connexion sur un autre
          // appareil) : retour à l'écran de connexion, pas de reconnexion.
          notifyNativeAuthLost();
          onError?.(new Error('Non authentifié'));
          return;
        }
        // Réseau KO : on retentera au rythme du backoff sans ouvrir un flux
        // voué au 401.
        onError?.(new Error('Renouvellement de session impossible'));
        reconnectTimer = setTimeout(connect, retryMs);
        retryMs = Math.min(retryMs * 2, MAX_RETRY_MS);
        return;
      }
    }
    const token = getToken();
    try {
      const res = await fetch(`${API_URL}${path}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        // Natif : pas de cookie (cf. nativeApiFetch).
        credentials: IS_NATIVE ? 'omit' : 'include',
        signal: ctrl.signal,
      });
      if (res.status === 401 && IS_NATIVE) {
        // Access token expiré : refresh partagé puis reconnexion. Si le refresh
        // a émis un nouveau jeton, `reopenNow` a déjà relancé (génération
        // changée) : on ne double pas la connexion.
        if (!nativeRefreshTried) {
          nativeRefreshTried = true;
          if (await ensureFreshNativeToken(token)) {
            if (!closed && myGeneration === generation) connect();
            return;
          }
        }
        if (myGeneration !== generation) return;
        notifyNativeAuthLost();
        onError?.(new Error('Non authentifié'));
        return; // pas de reconnexion sur 401
      }
      if (res.status === 401) {
        // F-S18-02 — idem apiFetch : préserver le token admin si impersonation.
        if (isImpersonating()) {
          endImpersonation();
          window.location.reload();
        } else {
          setToken(null);
        }
        onError?.(new Error('Non authentifié'));
        return; // pas de reconnexion sur 401
      }
      if (!res.ok || !res.body) throw new Error(`SSE HTTP ${res.status}`);

      onOpen?.();
      openedAt = Date.now();
      nativeRefreshTried = false;

      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      while (!closed) {
        const { value, done } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        let idx;
        while ((idx = buffer.indexOf('\n\n')) !== -1) {
          const block = buffer.slice(0, idx);
          buffer = buffer.slice(idx + 2);
          const evt = parseSseBlock(block);
          if (evt) onEvent?.(evt);
        }
      }
    } catch (err) {
      if (closed || ctrl.signal.aborted) return;
      onError?.(err);
    }
    // Natif : un flux abandonné par `reopenNow` a déjà été relancé.
    if (IS_NATIVE && (ctrl.signal.aborted || myGeneration !== generation)) return;
    if (!closed) {
      // Backoff remis à zéro seulement si la connexion a tenu (fermeture
      // normale à l'expiration du jeton ⇒ réouverture en 1 s).
      if (openedAt && Date.now() - openedAt >= STABLE_CONNECTION_MS) retryMs = 1000;
      reconnectTimer = setTimeout(connect, retryMs);
      retryMs = Math.min(retryMs * 2, MAX_RETRY_MS);
    }
  }

  connect();

  return {
    close() {
      closed = true;
      clearTimeout(reconnectTimer);
      controller?.abort();
      if (IS_NATIVE) {
        window.removeEventListener(NATIVE_TOKEN_EVENT, reopenNow);
        window.removeEventListener(NATIVE_RESUME_EVENT, reopenNow);
      }
    },
  };
}

/**
 * Origine absolue de l'API pour les routes OAuth (`/api/auth/google` + callback).
 *
 * En dev (Vite), le front tourne sur `localhost:5173` et l'API sur `localhost:4000`.
 * Si on passait par le proxy Vite (URL relative), le cookie `oauth_state_nonce` posé
 * par l'API serait attribué côté navigateur au domaine `localhost:5173`. Or Google
 * redirige le callback DIRECTEMENT sur `localhost:4000/api/auth/google/callback`
 * (selon `GOOGLE_OAUTH_REDIRECT_URI`) — donc le cookie n'est jamais renvoyé au
 * callback, le check CSRF échoue, et le user reçoit le message opaque
 * `oauth_failed` (F-AUTH-04 du hardening Sprint 1.0).
 *
 * Solution : faire visiter directement l'API par le navigateur pendant le flow
 * OAuth, pour que le cookie soit posé ET renvoyé sur le même origin.
 *
 * En prod, front et API partagent le même origin (reverse proxy) → URL relative OK.
 */
const API_OAUTH_ORIGIN = import.meta.env.DEV ? 'http://localhost:4000' : '';

/**
 * Lot C — SSO synotis-core.
 *
 * Le login passe désormais par le core (auth centralisée Google + cookie de
 * session cross-subdomain). On redirige le navigateur vers
 * `${CORE_BASE_URL}/auth/google?origin=<origin Synolog>&next=…`. Le core
 * authentifie, pose `syn_session` + `syn_csrf` sur `.synotis.tech`, puis
 * renvoie sur `origin + next`.
 *
 * `VITE_CORE_BASE_URL` configure la base du core (défaut dev `localhost:4100`).
 */
const CORE_BASE_URL = import.meta.env.VITE_CORE_BASE_URL || 'http://localhost:4100';

export function loginUrl(next = '/') {
  const url = new URL('/auth/google', CORE_BASE_URL);
  url.searchParams.set('origin', window.location.origin);
  url.searchParams.set('next', next);
  return url.toString();
}

export const apiAuth = {
  me: () => apiFetch('/auth/me'),
  logout: () => apiFetch('/auth/logout', { method: 'POST' }),
  stopImpersonation: () => apiFetch('/auth/stop-impersonation', { method: 'POST' }),
  /** Lot C — login via SSO core (remplace l'OAuth local). */
  loginUrl,
  /** Conservé pour bascule d'urgence vers l'OAuth Google LOCAL (dormant). */
  googleLoginUrl: (next = '/') =>
    `${API_OAUTH_ORIGIN}/api/auth/google?next=${encodeURIComponent(next)}`,
};

export const apiHealth = () => apiFetch('/health');

/**
 * Sprint App native — contrat §7. Configuration publique de l'app native
 * (version minimale, URLs des stores). Sans authentification.
 */
export const apiMobile = {
  config: () => apiFetch('/mobile/config'),
  // #93 — « Mon mois » de la Person du compte (imputations, planning, statuts).
  summary: () => apiFetch('/mobile/summary'),
  // #93 — abonnement de l'appareil aux rappels push (jeton APNs).
  registerPushToken: (payload) =>
    apiFetch('/mobile/push-token', { method: 'POST', body: JSON.stringify(payload) }),
  unregisterPushToken: (token) =>
    apiFetch('/mobile/push-token', { method: 'DELETE', body: JSON.stringify({ token }) }),
};

/**
 * Factory de CRUD pour les entités métier (Sprint 1.1).
 * Toutes les routes sont sous /api/<path>, conforme aux conventions backend.
 * Réponses :
 *  - list()        → { items: Entity[] }
 *  - get(id)       → Entity
 *  - create(data)  → Entity (201)
 *  - update(id, d) → Entity
 *  - archive(id)   → null (204) — soft-delete
 *  - restore(id)   → Entity
 */
function entityCrud(path) {
  return {
    list: (archived = 'active') =>
      apiFetch(`/${path}?archived=${encodeURIComponent(archived)}`),
    get: (id) => apiFetch(`/${path}/${encodeURIComponent(id)}`),
    create: (data) =>
      apiFetch(`/${path}`, { method: 'POST', body: JSON.stringify(data) }),
    update: (id, data) =>
      apiFetch(`/${path}/${encodeURIComponent(id)}`, {
        method: 'PATCH',
        body: JSON.stringify(data),
      }),
    archive: (id) =>
      apiFetch(`/${path}/${encodeURIComponent(id)}`, { method: 'DELETE' }),
    restore: (id) =>
      apiFetch(`/${path}/${encodeURIComponent(id)}/restore`, { method: 'POST' }),
  };
}

/**
 * Clients — référentiel MAÎTRISÉ par synocore (drapeau `CLIENT_MASTER_CORE`).
 *
 * Les écritures de l'écran admin sont en WRITE-THROUGH : le nom part vers le
 * référentiel central, puis redescend dans le miroir local ; la couleur reste
 * strictement locale. Conséquence pour le front : `create`, `update`, `archive`
 * et `restore` peuvent répondre `{ client, mirror: { status, reason } }` au lieu
 * de l'entité nue — `mirror.status !== 'OK'` signifie « enregistré au central,
 * réplication locale en attente », jamais « échec ». Le helper `unwrap`
 * ci-dessous absorbe les deux formes, pour que le comportement historique
 * (drapeau baissé) reste inchangé.
 */
export const apiClients = {
  ...entityCrud('clients'),
  /** Repeint en masse les affaires listées (admin only).
   *  color : hex "RRGGBB" sans `#`, ou `null` pour effacer. */
  recolorAffaires: (clientId, color, affaireIds) =>
    apiFetch(`/clients/${encodeURIComponent(clientId)}/recolor-affaires`, {
      method: 'POST',
      body: JSON.stringify({ color, affaireIds }),
    }),
  /**
   * Capacités de l'écran (admin) : `{ clientMasterCore }`.
   *
   * Le front NE DOIT PAS deviner l'état du drapeau : sans cet appel, le bouton
   * « Synchroniser » s'affiche sur un déploiement où la descente n'existe pas
   * encore et ne peut que répondre une erreur.
   */
  meta: () => apiFetch('/clients/meta'),
  /** Rejoue la descente CIBLÉE d'un client (bouton « Réessayer »). */
  pull: (clientId) =>
    apiFetch(`/clients/${encodeURIComponent(clientId)}/pull`, { method: 'POST' }),
  /**
   * Relance la descente COMPLÈTE du périmètre rattaché (admin only).
   *
   * Répond 202 : la descente part détachée, le résultat se lit dans le journal
   * unifié (console synobatch). Un 409 signifie soit qu'une descente est déjà
   * en cours, soit que le référentiel n'est pas maîtrisé par synocore.
   */
  sync: () => apiFetch('/clients/sync', { method: 'POST' }),
  /** #108 — Responsables du client (admin) : `{ items: [{ personId, firstName, lastName, archived }] }`. */
  listManagers: (clientId) => apiFetch(`/clients/${encodeURIComponent(clientId)}/managers`),
  /** #108 — Remplace la liste complète des responsables (admin). */
  setManagers: (clientId, personIds) =>
    apiFetch(`/clients/${encodeURIComponent(clientId)}/managers`, {
      method: 'PUT',
      body: JSON.stringify({ personIds }),
    }),
  /** Normalise une réponse write-through vers `{ client, mirror }`. */
  unwrap: (res) =>
    res && typeof res === 'object' && 'mirror' in res
      ? { client: res.client ?? null, mirror: res.mirror ?? null }
      : { client: res ?? null, mirror: null },
};
// Sprint 8 — surcharge `list` pour propager le filterQuery (système faceted filter).
// Sprint 7.2 — option `mineOnly` : restreint aux affaires de la Person liée au
// user courant par email (membre actif d'équipe OR project manager OR a loggué).
// ADMIN bypass côté backend (voit tout même avec mineOnly=true).
export const apiAffaires = {
  ...entityCrud('affaires'),
  list: (archived = 'active', filter = null, opts = {}) => {
    const params = new URLSearchParams({ archived });
    appendFilterParam(params, filter);
    if (opts.mineOnly) params.set('mineOnly', '1');
    // Ticket #29 — liste des affaires imputables PAR UNE AUTRE Person (manager
    // saisissant pour un subordonné). Le backend vérifie `canManagePerson`.
    if (opts.forPersonId) params.set('forPersonId', opts.forPersonId);
    return apiFetch(`/affaires?${params.toString()}`);
  },
  // Suppression DÉFINITIVE (hard delete) — ADMIN, affaire déjà archivée.
  destroy: (id) =>
    apiFetch(`/affaires/${encodeURIComponent(id)}/permanent`, { method: 'DELETE' }),
  // synotis-support#19 — candidates à l'archivage (ADMIN, lecture seule).
  // → { inactiveMonths, today, cutoffDate, total, items }
  archiveSuggestions: (inactiveMonths = 3) =>
    apiFetch(`/affaires/archive-suggestions?inactiveMonths=${encodeURIComponent(inactiveMonths)}`),
};
// Ticket #54 — Affaires INTERNES gérées par un manager (ADMIN : toutes).
// Payload réduit (name, notes, color, startDate, endDate, expiresAt) : tout
// autre champ est refusé en 400 par le serveur, qui force le reste.
export const apiInternalAffaires = {
  list: (archived = 'active') =>
    apiFetch(`/affaires/internal?archived=${encodeURIComponent(archived)}`),
  create: (payload) =>
    apiFetch('/affaires/internal', { method: 'POST', body: JSON.stringify(payload) }),
  update: (id, payload) =>
    apiFetch(`/affaires/internal/${encodeURIComponent(id)}`, {
      method: 'PATCH',
      body: JSON.stringify(payload),
    }),
  archive: (id) =>
    apiFetch(`/affaires/internal/${encodeURIComponent(id)}`, { method: 'DELETE' }),
  restore: (id) =>
    apiFetch(`/affaires/internal/${encodeURIComponent(id)}/restore`, { method: 'POST' }),
};
export const apiPersons = {
  ...entityCrud('persons'),
  list: (archived = 'active', filter = null) => {
    const params = new URLSearchParams({ archived });
    appendFilterParam(params, filter);
    return apiFetch(`/persons?${params.toString()}`);
  },
  // Sprint 14 — Person liée au compte courant (404 si aucune).
  me: () => apiFetch('/persons/me'),
  // Tags distincts existants (autocomplete de l'éditeur de tags).
  tags: () => apiFetch('/persons/tags'),
  /** BU distinctes déjà utilisées (liste de choix de la fiche personne). */
  bus: () => apiFetch('/persons/bus'),
  // Import CSV GESCOM 430. `apply=false` → dry-run (plan). `enrichExisting` →
  // complète les champs Penta/CRM vides des fiches existantes (non destructif).
  // `selectedCreateKeys`/`selectedEnrichIds` (optionnels) restreignent
  // l'application aux lignes cochées dans la modale.
  importGescom: (csv, { apply = false, enrichExisting = false, selectedCreateKeys, selectedEnrichIds } = {}) =>
    apiFetch('/persons/import/gescom', {
      method: 'POST',
      body: JSON.stringify({ csv, apply, enrichExisting, selectedCreateKeys, selectedEnrichIds }),
    }),
  // Upload avatar — demande une URL presigned-PUT (MinIO/S3) + l'URL publique.
  getAvatarUploadUrl: (personId, { filename, contentType, fileSize } = {}) =>
    apiFetch(`/persons/${encodeURIComponent(personId)}/avatar-upload-url`, {
      method: 'POST',
      body: JSON.stringify({ filename, contentType, fileSize }),
    }),
};

// Upload avatar — contraintes partagées avec le backend (lib/s3.ts).
export const AVATAR_ACCEPTED_TYPES = ['image/jpeg', 'image/png', 'image/webp'];
export const AVATAR_MAX_BYTES = 5 * 1024 * 1024;

/**
 * Upload d'un fichier avatar : valide type + taille, demande une URL
 * presigned-PUT au backend, pousse le fichier DIRECTEMENT sur MinIO/S3 via
 * `fetch` (PAS via apiFetch — c'est une URL S3 signée, pas notre API), et
 * retourne l'URL publique permanente (à persister telle quelle dans
 * `Person.avatarUrl`, sans cache-bust).
 */
export async function uploadAvatarFile(personId, file) {
  if (!AVATAR_ACCEPTED_TYPES.includes(file.type)) {
    throw new Error('Format non supporté (JPEG, PNG ou WebP attendu).');
  }
  if (file.size > AVATAR_MAX_BYTES) {
    throw new Error('Fichier trop volumineux (max 5 Mo).');
  }
  const { uploadUrl, publicUrl } = await apiPersons.getAvatarUploadUrl(personId, {
    filename: file.name,
    contentType: file.type,
    fileSize: file.size,
  });
  const res = await fetch(uploadUrl, {
    method: 'PUT',
    headers: { 'Content-Type': file.type },
    body: file,
  });
  if (!res.ok) {
    throw new Error(`Échec de l'upload (${res.status}).`);
  }
  return publicUrl;
}
/**
 * Sprint 9 — Hiérarchie managériale (PersonManager M2M self).
 *
 * - `listManagers(personId)`     → managers directs.
 * - `listAllManagers(personId)`  → ascendance complète (CTE, avec depth).
 * - `listReports(personId)`      → subordonnés directs.
 * - `listAllReports(personId)`   → descendance complète (CTE, avec depth).
 * - `assign(personId, managerId)` (ADMIN) → ajoute un lien.
 * - `remove(personId, managerId)` (ADMIN) → retire un lien.
 *
 * Lecture : ouverte au scope du user (ADMIN partout, MANAGER sa descendance,
 * USER/VIEWER sa propre Person).
 */
export const apiPersonManagers = {
  listManagers: (personId) => apiFetch(`/persons/${personId}/managers`),
  listAllManagers: (personId) => apiFetch(`/persons/${personId}/managers/all`),
  listReports: (personId) => apiFetch(`/persons/${personId}/managers/reports`),
  listAllReports: (personId) => apiFetch(`/persons/${personId}/managers/reports/all`),
  assign: (personId, managerId) =>
    apiFetch(`/persons/${personId}/managers`, {
      method: 'POST',
      body: JSON.stringify({ managerId }),
    }),
  remove: (personId, managerId) =>
    apiFetch(`/persons/${personId}/managers/${managerId}`, { method: 'DELETE' }),
  // Sprint 14 — snapshot org (persons + liens) pour la vue tableau drag&drop.
  overview: () => apiFetch('/persons/hierarchy/overview'),
};

/**
 * Disponibilité datée — versions de planning hebdomadaire d'une Person.
 * Une version s'applique de sa `effectiveDate` jusqu'à la suivante. Créer une
 * version clôt l'actuelle. `effectiveDate` au format "YYYY-MM-DD".
 *
 * Lecture : ADMIN, soi-même, MANAGER sur descendance. Mutation : ADMIN ou
 * MANAGER sur sa descendance/soi.
 */
export const apiPersonWorkSchedule = {
  list: (personId) => apiFetch(`/persons/${personId}/work-schedule`),
  create: (personId, data) =>
    apiFetch(`/persons/${personId}/work-schedule`, {
      method: 'POST',
      body: JSON.stringify(data),
    }),
  update: (personId, versionId, data) =>
    apiFetch(`/persons/${personId}/work-schedule/${versionId}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    }),
  remove: (personId, versionId) =>
    apiFetch(`/persons/${personId}/work-schedule/${versionId}`, { method: 'DELETE' }),
};
export const apiTimeOffTypes = entityCrud('time-off-types');
export const apiDepartments = entityCrud('departments');
export const apiRoles = entityCrud('roles');
/**
 * Sprint 2.A + Sprint 8 — Allocations CRUD + EditScope sur occurrences.
 *
 * `patchInstance(id, occurrenceDate, scope, data)` : modifie une occurrence
 *   d'une série récurrente avec le scope choisi (this_occurrence /
 *   this_and_future / all_occurrences).
 * `deleteInstance(id, occurrenceDate, scope)` : supprime selon le même scope.
 *
 * `occurrenceDate` au format "YYYY-MM-DD" (date ORIGINALE de l'occurrence,
 * avant tout déplacement via override).
 */
export const apiAllocations = {
  ...entityCrud('allocations'),
  patchInstance: (id, occurrenceDate, scope, data) =>
    apiFetch(
      `/allocations/${encodeURIComponent(id)}/instances/${encodeURIComponent(occurrenceDate)}?scope=${encodeURIComponent(scope)}`,
      { method: 'PATCH', body: JSON.stringify(data) },
    ),
  deleteInstance: (id, occurrenceDate, scope) =>
    apiFetch(
      `/allocations/${encodeURIComponent(id)}/instances/${encodeURIComponent(occurrenceDate)}?scope=${encodeURIComponent(scope)}`,
      { method: 'DELETE' },
    ),
};

/**
 * Sprint 5 — Country / Region / PublicHoliday.
 *
 * `apiCountries` : CRUD standard (PK = code à 2 lettres, transmis dans l'URL).
 * `apiRegions`   : list filtrable par countryCode pour le picker.
 * `apiPublicHolidays` : list filtrable par countryCode/regionCode/year +
 *                       endpoint utilitaire `forPerson(personId, from, to)`.
 */
/**
 * Ticket #6 — Statut de présence / lieu de travail par jour (autonome).
 */
export const apiWorkLocations = {
  // Ticket #91 — `signal` : annulation d'un segment devenu inutile.
  list: ({ from, to, signal }) => {
    const params = new URLSearchParams({ from, to });
    return apiFetch(`/work-locations?${params.toString()}`, { signal });
  },
  set: (data) => apiFetch('/work-locations/set', { method: 'POST', body: JSON.stringify(data) }),
  clear: (data) => apiFetch('/work-locations/clear', { method: 'POST', body: JSON.stringify(data) }),
};

export const apiCountries = entityCrud('countries');

export const apiRegions = {
  ...entityCrud('regions'),
  list: (countryCode, archived = 'active') => {
    const params = new URLSearchParams({ archived });
    if (countryCode) params.set('countryCode', countryCode);
    return apiFetch(`/regions?${params.toString()}`);
  },
};

export const apiPublicHolidays = {
  ...entityCrud('public-holidays'),
  list: ({ countryCode, regionCode, year, archived = 'active' } = {}) => {
    const params = new URLSearchParams({ archived });
    if (countryCode) params.set('countryCode', countryCode);
    if (regionCode) params.set('regionCode', regionCode);
    if (year) params.set('year', String(year));
    return apiFetch(`/public-holidays?${params.toString()}`);
  },
  forPerson: (personId, from, to) =>
    apiFetch(
      `/public-holidays/for-person/${encodeURIComponent(personId)}?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}`,
    ),
};

/**
 * Sprint 3 — saisie horaire quotidienne.
 * Filtres optionnels: personId, affaireId, from=YYYY-MM-DD, to=YYYY-MM-DD,
 * archived=active|archived|all (défaut 'active').
 *
 * Sprint 6 : ajout de `bulkCreate(entries)` pour valider un batch de
 * suggestions (bouton "Log all").
 */
export const apiLoggedTime = {
  list: ({ personId, affaireId, from, to, archived = 'active', filter } = {}) => {
    const params = new URLSearchParams();
    if (personId) params.set('personId', personId);
    if (affaireId) params.set('affaireId', affaireId);
    if (from) params.set('from', from);
    if (to) params.set('to', to);
    if (archived) params.set('archived', archived);
    appendFilterParam(params, filter);
    return apiFetch(`/logged-time?${params.toString()}`);
  },
  get: (id) => apiFetch(`/logged-time/${encodeURIComponent(id)}`),
  create: (data) =>
    apiFetch('/logged-time', { method: 'POST', body: JSON.stringify(data) }),
  update: (id, data) =>
    apiFetch(`/logged-time/${encodeURIComponent(id)}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    }),
  archive: (id) =>
    apiFetch(`/logged-time/${encodeURIComponent(id)}`, { method: 'DELETE' }),
  restore: (id) =>
    apiFetch(`/logged-time/${encodeURIComponent(id)}/restore`, { method: 'POST' }),
  // Sprint 6 — bulk create idempotent. Retourne { created, skipped, totalHours }.
  bulkCreate: (entries) =>
    apiFetch('/logged-time/bulk', {
      method: 'POST',
      body: JSON.stringify({ entries }),
    }),
};

/**
 * Sprint 6 — Verrou comptable Logtime.
 *  - current() : retourne le lock actif (ou null).
 *  - set(lockDate, reason) : pose un nouveau lock. ADMIN seul côté backend.
 */
export const apiLogTimeLock = {
  // État complet : { lockDate (cutoff effectif), manual, autoLock, grants }.
  current: () => apiFetch('/log-time-lock'),
  set: (lockDate, reason) =>
    apiFetch('/log-time-lock', {
      method: 'PUT',
      body: JSON.stringify({ lockDate, reason: reason ?? null }),
    }),
  // Sprint 20 — exemptions de déverrouillage temporaire (ADMIN).
  listGrants: (scope = 'active') => apiFetch(`/log-time-lock/grants?scope=${scope}`),
  createGrant: (payload) =>
    apiFetch('/log-time-lock/grants', { method: 'POST', body: JSON.stringify(payload) }),
  revokeGrant: (id) =>
    apiFetch(`/log-time-lock/grants/${id}/revoke`, { method: 'POST' }),
};

/**
 * Sprint 11 — Paramètres applicatifs globaux (feature flags).
 *  - get()        → { selfTimeOffRequestEnabled, ... } (lecture ACTIVE)
 *  - update(patch) → PUT /api/settings (ADMIN), retourne l'état complet.
 */
export const apiSettings = {
  get: () => apiFetch('/settings'),
  update: (patch) =>
    apiFetch('/settings', { method: 'PUT', body: JSON.stringify(patch) }),
};

/**
 * Gestion des comptes utilisateurs (ADMIN only).
 *  - list({ status, role, q }) → { items } (PENDING d'abord)
 *  - update(id, { status?, role? }) → compte mis à jour
 *  - activate / disable / setRole : raccourcis.
 */
export const apiUsers = {
  list: ({ status, role, q } = {}) => {
    const params = new URLSearchParams();
    if (status) params.set('status', status);
    if (role) params.set('role', role);
    if (q) params.set('q', q);
    const qs = params.toString();
    return apiFetch(`/users${qs ? `?${qs}` : ''}`);
  },
  get: (id) => apiFetch(`/users/${id}`),
  update: (id, patch) =>
    apiFetch(`/users/${id}`, { method: 'PATCH', body: JSON.stringify(patch) }),
  activate: (id) =>
    apiFetch(`/users/${id}`, { method: 'PATCH', body: JSON.stringify({ status: 'ACTIVE' }) }),
  disable: (id) =>
    apiFetch(`/users/${id}`, { method: 'PATCH', body: JSON.stringify({ status: 'DISABLED' }) }),
  setRole: (id, role) =>
    apiFetch(`/users/${id}`, { method: 'PATCH', body: JSON.stringify({ role }) }),
  /** Impersonation : retourne { token, user } pour le compte cible. */
  impersonate: (id) => apiFetch(`/users/${id}/impersonate`, { method: 'POST' }),
};

/**
 * Sprint 13 — Tableau de bord (Accueil).
 * get(scope) → { config, scope, availableScopes, myPersonId, window,
 *   personal, team }. `scope` (self|team|all) est borné par le rôle côté
 * serveur — un USER ne peut jamais élargir.
 */
export const apiDashboard = {
  get: (scope) => {
    const params = new URLSearchParams();
    if (scope) params.set('scope', scope);
    const qs = params.toString();
    return apiFetch(`/dashboard${qs ? `?${qs}` : ''}`);
  },
  /**
   * Ticket #49 — détail d'une tuile d'accueil : jours concernés par
   * l'indicateur (`scheduling`, `schedulingMonth`, `prevWeek`, `currentWeek`).
   */
  detail: (metric, scope) => {
    const params = new URLSearchParams({ metric });
    if (scope) params.set('scope', scope);
    return apiFetch(`/dashboard/detail?${params.toString()}`);
  },
};

/**
 * Reports & Analytics. Le scope est borné par le rôle côté serveur.
 *  utilization({ from, to, period, viewMode, scope, filter })
 *    → { viewMode, period, from, to, filtersApplied, series, totals, meta }
 * `from`/`to` : Date | string ISO 'YYYY-MM-DD'. `filter` : filterQuery facetté.
 */
export const apiReports = {
  utilization: ({ from, to, period, viewMode, scope, teamDepth, filter, includeWeekends } = {}) => {
    const params = new URLSearchParams();
    const f = isoOrString(from);
    const t = isoOrString(to);
    if (f) params.set('from', f.slice(0, 10));
    if (t) params.set('to', t.slice(0, 10));
    if (period) params.set('period', period);
    if (viewMode) params.set('viewMode', viewMode);
    if (scope) params.set('scope', scope);
    if (teamDepth) params.set('teamDepth', teamDepth);
    if (includeWeekends !== undefined) params.set('includeWeekends', includeWeekends ? 'true' : 'false');
    if (filter) appendFilterParam(params, filter);
    const qs = params.toString();
    return apiFetch(`/reports/utilization${qs ? `?${qs}` : ''}`);
  },
  // Sprint 22 — breakdown par dimension (onglets Float). Scope borné serveur.
  // dimension ∈ people|roles|departments|projects|tasks|timeoff|timetracking
  breakdown: ({ dimension, from, to, period, viewMode, scope, teamDepth, filter, includeWeekends } = {}) => {
    const params = new URLSearchParams();
    if (dimension) params.set('dimension', dimension);
    const f = isoOrString(from);
    const t = isoOrString(to);
    if (f) params.set('from', f.slice(0, 10));
    if (t) params.set('to', t.slice(0, 10));
    if (period) params.set('period', period);
    if (viewMode) params.set('viewMode', viewMode);
    if (scope) params.set('scope', scope);
    if (teamDepth) params.set('teamDepth', teamDepth);
    if (includeWeekends !== undefined) params.set('includeWeekends', includeWeekends ? 'true' : 'false');
    if (filter) appendFilterParam(params, filter);
    const qs = params.toString();
    return apiFetch(`/reports/breakdown${qs ? `?${qs}` : ''}`);
  },
  // Ticket #56 — suivi du temps par affaire (Accueil, tous rôles). Périmètre
  // d'affaires borné serveur. #113 : `personId` optionnel.
  // → { from, to, personId, rows, totals, affaires, persons, myPersonId }
  affaireTime: ({ from, to, affaireIds, personId } = {}) => {
    const params = new URLSearchParams();
    if (from) params.set('from', String(from).slice(0, 10));
    if (to) params.set('to', String(to).slice(0, 10));
    if (affaireIds && affaireIds.length > 0) params.set('affaireIds', affaireIds.join(','));
    if (personId) params.set('personId', personId);
    return apiFetch(`/reports/affaire-time?${params.toString()}`);
  },
  // Ticket #113 (lot 2) — relevé d'heures d'une personne (CERFA 2041-AE).
  // `personId` optionnel (sa propre fiche par défaut), borné serveur (404 hors
  // périmètre). → { person, from, to, categories, workedHours, timeOffHours,
  //   totalHours, persons, myPersonId }
  timeStatement: ({ from, to, personId } = {}) => {
    const params = new URLSearchParams();
    if (from) params.set('from', String(from).slice(0, 10));
    if (to) params.set('to', String(to).slice(0, 10));
    if (personId) params.set('personId', personId);
    return apiFetch(`/reports/time-statement?${params.toString()}`);
  },
  // synotis-support#13 — rapport « Projets » (niveau délégué #108, borné serveur).
  // → { rows, clients } ; montants agrégés à l'affaire, jamais de coût ni marge.
  projects: ({ clientId, kind, archived, status } = {}) => {
    const params = new URLSearchParams();
    if (clientId) params.set('clientId', clientId);
    if (kind) params.set('kind', kind);
    if (archived) params.set('archived', archived);
    if (status) params.set('status', status);
    const qs = params.toString();
    return apiFetch(`/reports/projects${qs ? `?${qs}` : ''}`);
  },
  // → { from, to, today, granularity, budgetTotal, summary, series, people, tasks, phases }
  projectDetail: (id, { from, to, granularity } = {}) => {
    const params = new URLSearchParams();
    if (from) params.set('from', String(from).slice(0, 10));
    if (to) params.set('to', String(to).slice(0, 10));
    if (granularity) params.set('granularity', granularity);
    const qs = params.toString();
    return apiFetch(`/reports/projects/${encodeURIComponent(id)}${qs ? `?${qs}` : ''}`);
  },
  // synotis-support#34 — imputations d'un projet, paginées et triées serveur.
  // → { total, totalHours, page, pageSize, sort, dir, rows } ; heures seules.
  projectTimeEntries: (id, { from, to, personId, sort, dir, page, pageSize } = {}) => {
    const params = new URLSearchParams();
    if (from) params.set('from', String(from).slice(0, 10));
    if (to) params.set('to', String(to).slice(0, 10));
    if (personId) params.set('personId', personId);
    if (sort) params.set('sort', sort);
    if (dir) params.set('dir', dir);
    if (page) params.set('page', String(page));
    if (pageSize) params.set('pageSize', String(pageSize));
    const qs = params.toString();
    return apiFetch(`/reports/projects/${encodeURIComponent(id)}/time-entries${qs ? `?${qs}` : ''}`);
  },
};

/**
 * Sprint 23 — Budget & alertes (calculées à la volée, pas de modèle).
 *  - alerts()      → { threshold, alerts: [{ affaireId, name, code, budgetTotal,
 *                      consumedChf, consumptionPct, status }] } scopé serveur.
 *  - affaire(id)   → { threshold, affaireId, budgetTotal, consumedChf,
 *                      consumptionPct, status } (badge fiche/liste).
 */
export const apiBudget = {
  alerts: () => apiFetch('/budget/alerts'),
  affaire: (id) => apiFetch(`/budget/affaire/${id}`),
  // Badges de la liste des affaires en un appel : { items: [{ affaireId,
  // budgetTotal, consumedChf, consumptionPct, status }] } (sans projection).
  statuses: (archived = 'active') =>
    apiFetch(`/budget/statuses?archived=${encodeURIComponent(archived)}`),
  // Vue succincte collaborateur : { threshold, items: [{ affaireId, name, code,
  // status, consumptionPct, remainingDays, isAlert }] } — aucun montant.
  myAffaires: () => apiFetch('/budget/my-affaires'),
};

/**
 * Sprint 13 — Audit LECTURE SEULE (ADMIN + MANAGER ; manager scopé descendance).
 *  - list(params)  → { items, nextCursor, hasMore } (cursor, plus récent d'abord)
 *  - actions()     → { items: [{ action, count }] }
 *  - stats(params) → agrégations dashboard
 * `from`/`to` : Date | string ISO ('YYYY-MM-DD').
 */
function isoOrString(v) {
  if (v == null) return undefined;
  if (v instanceof Date) return v.toISOString();
  return String(v);
}

export const apiAdminBackups = {
  // GET /api/admin/backups — historique + résumé par environnement (ADMIN).
  list: () => apiFetch('/admin/backups'),
};

/**
 * #63 — File d'arbitrage des affaires sans projet Gescom (LECTURE SEULE, ADMIN).
 * Normalisation camelCase → snake_case pour la vue.
 */
function normalizeAffaireToArbitrate(a) {
  return {
    id: a.id,
    name: a.name,
    ch_code: a.chCode ?? null,
    client_name: a.clientName ?? '',
    status: a.status,
    planning_only: Boolean(a.planningOnly),
    recent_logged_times: a.recentLoggedTimes ?? 0,
    recent_hours: a.recentHours ?? 0,
    last_logged_at: a.lastLoggedAt ?? null,
  };
}

export const apiAdminGescomAffaires = {
  // GET /api/admin/gescom-affaires/arbitrage?days=N
  arbitrage: async (days = 90) => {
    const res = await apiFetch(`/admin/gescom-affaires/arbitrage?days=${encodeURIComponent(days)}`);
    return {
      days: res.days,
      total: res.total,
      with_recent_activity: res.withRecentActivity,
      items: (res.items ?? []).map(normalizeAffaireToArbitrate),
    };
  },
};

export const apiAudit = {
  list: (params = {}) => {
    const q = new URLSearchParams();
    if (params.limit != null) q.set('limit', String(params.limit));
    if (params.cursor) q.set('cursor', params.cursor);
    if (params.action) q.set('action', params.action);
    if (params.actionPrefix) q.set('actionPrefix', params.actionPrefix);
    if (params.userId) q.set('userId', params.userId);
    if (params.oauthClientId) q.set('oauthClientId', params.oauthClientId);
    const from = isoOrString(params.from);
    const to = isoOrString(params.to);
    if (from) q.set('from', from);
    if (to) q.set('to', to);
    if (params.source) q.set('source', params.source);
    const qs = q.toString();
    return apiFetch(`/audit${qs ? `?${qs}` : ''}`);
  },
  actions: () => apiFetch('/audit/actions'),
  stats: (params = {}) => {
    const q = new URLSearchParams();
    const from = isoOrString(params.from);
    const to = isoOrString(params.to);
    if (from) q.set('from', from);
    if (to) q.set('to', to);
    const qs = q.toString();
    return apiFetch(`/audit/stats${qs ? `?${qs}` : ''}`);
  },
};

/**
 * Endpoint optimisé pour la vue de saisie hebdo (Sprint 3).
 * Retourne person + days + allocationsByDay + loggedTimesByDay + totalsByDay.
 */
export const apiLogtime = {
  week: ({ weekStart, personId } = {}) => {
    const params = new URLSearchParams();
    if (weekStart) params.set('weekStart', weekStart);
    if (personId) params.set('personId', personId);
    return apiFetch(`/logtime/week?${params.toString()}`);
  },
  // Sprint 12 — vue calendaire de validation du prévisionnel. Retourne
  // { persons, allocations (instances expansées), loggedTimes, lock,
  //   holidaysByPerson? } scopés par rôle (USER own / MANAGER équipe / ADMIN).
  // Ticket #91 — `signal` : annulation d'un segment devenu inutile.
  calendar: ({ from, to, withHolidays, withOutOfFilter, scope, filter, signal } = {}) => {
    const params = new URLSearchParams();
    if (from) params.set('from', from);
    if (to) params.set('to', to);
    if (withHolidays) params.set('withHolidays', '1');
    if (withOutOfFilter) params.set('withOutOfFilter', '1');
    if (scope) params.set('scope', scope);
    appendFilterParam(params, filter);
    return apiFetch(`/logtime/calendar?${params.toString()}`, { signal });
  },
  // Ticket #23 — « Supprimer » un jour planifié dans la saisie masque le square
  // prévisionnel sans toucher au scheduling (l'allocation reste intacte).
  // Ticket #76 — `taskId` fait partie de l'identité du square : le schéma
  // serveur est `.strict()`, le champ doit donc être transmis explicitement.
  dismissSquare: ({ personId, affaireId, phaseId, taskId, date }) =>
    apiFetch('/logtime/dismiss', {
      method: 'POST',
      body: JSON.stringify({
        personId,
        affaireId,
        phaseId: phaseId ?? null,
        taskId: taskId ?? null,
        date,
      }),
    }),
  // Réaffiche un square précédemment supprimé (lève le marqueur).
  undismissSquare: ({ personId, affaireId, phaseId, taskId, date }) =>
    apiFetch('/logtime/dismiss', {
      method: 'DELETE',
      body: JSON.stringify({
        personId,
        affaireId,
        phaseId: phaseId ?? null,
        taskId: taskId ?? null,
        date,
      }),
    }),
};

/**
 * Sprint 2.A + 4 — TimeOffs avec extension workflow validation.
 *  - approve(id, note) → POST /:id/approve
 *  - reject(id, note)  → POST /:id/reject
 */
export const apiTimeOffs = {
  ...entityCrud('timeoffs'),
  approve: (id, note) =>
    apiFetch(`/timeoffs/${encodeURIComponent(id)}/approve`, {
      method: 'POST',
      body: JSON.stringify({ note: note ?? null }),
    }),
  reject: (id, note) =>
    apiFetch(`/timeoffs/${encodeURIComponent(id)}/reject`, {
      method: 'POST',
      body: JSON.stringify({ note: note ?? null }),
    }),
  // Sprint 8 — EditScope sur occurrences (mêmes 3 modes que Allocation).
  patchInstance: (id, occurrenceDate, scope, data) =>
    apiFetch(
      `/timeoffs/${encodeURIComponent(id)}/instances/${encodeURIComponent(occurrenceDate)}?scope=${encodeURIComponent(scope)}`,
      { method: 'PATCH', body: JSON.stringify(data) },
    ),
  deleteInstance: (id, occurrenceDate, scope) =>
    apiFetch(
      `/timeoffs/${encodeURIComponent(id)}/instances/${encodeURIComponent(occurrenceDate)}?scope=${encodeURIComponent(scope)}`,
      { method: 'DELETE' },
    ),
};

/**
 * Sprint 7 — Project Team Members (pattern Float : overrides par affaire).
 *  - list(affaireId, archived)         → GET /api/affaires/:id/team
 *  - get(affaireId, memberId)          → GET /api/affaires/:id/team/:mid
 *  - create(affaireId, payload)        → POST  /api/affaires/:id/team
 *  - update(affaireId, mid, payload)   → PATCH /api/affaires/:id/team/:mid
 *  - remove(affaireId, mid)            → DELETE /api/affaires/:id/team/:mid
 *  - restore(affaireId, mid)           → POST /api/affaires/:id/team/:mid/restore
 *  - available(affaireId, q, limit)    → GET /api/affaires/:id/team/available
 */
export const apiAffaireTeam = {
  list: (affaireId, archived = 'active') =>
    apiFetch(
      `/affaires/${encodeURIComponent(affaireId)}/team?archived=${encodeURIComponent(archived)}`,
    ),
  get: (affaireId, memberId) =>
    apiFetch(
      `/affaires/${encodeURIComponent(affaireId)}/team/${encodeURIComponent(memberId)}`,
    ),
  create: (affaireId, payload) =>
    apiFetch(`/affaires/${encodeURIComponent(affaireId)}/team`, {
      method: 'POST',
      body: JSON.stringify(payload),
    }),
  update: (affaireId, memberId, payload) =>
    apiFetch(
      `/affaires/${encodeURIComponent(affaireId)}/team/${encodeURIComponent(memberId)}`,
      { method: 'PATCH', body: JSON.stringify(payload) },
    ),
  remove: (affaireId, memberId) =>
    apiFetch(
      `/affaires/${encodeURIComponent(affaireId)}/team/${encodeURIComponent(memberId)}`,
      { method: 'DELETE' },
    ),
  restore: (affaireId, memberId) =>
    apiFetch(
      `/affaires/${encodeURIComponent(affaireId)}/team/${encodeURIComponent(memberId)}/restore`,
      { method: 'POST' },
    ),
  available: (affaireId, q, limit = 20) => {
    const params = new URLSearchParams();
    if (q) params.set('q', q);
    if (limit) params.set('limit', String(limit));
    return apiFetch(
      `/affaires/${encodeURIComponent(affaireId)}/team/available?${params.toString()}`,
    );
  },
};

/**
 * Sprint 7 — Marge agrégée d'une affaire (LoggedTime × effective_rate).
 *  Retour : { affaireId, totalHours, totalCost, totalRevenue, totalMargin,
 *            averageMarginPerHour, contributorCount }
 */
export const apiAffaireMargin = {
  get: (affaireId) => apiFetch(`/affaires/${encodeURIComponent(affaireId)}/margin`),
};

/**
 * Phases (lecture seule, Sprint 2.D) — utilisé par le picker de la modale
 * création / édition d'allocation. Les phases sont importées via Float, pas
 * de CRUD UI à ce stade.
 */
/**
 * Ticket #76 — référentiel de tâches PARTAGÉ par affaire (la « Task » de
 * Float). Lecture ouverte à tout compte actif ; création/renommage/archivage
 * ouverts à tout compte en écriture (pas réservés aux ADMIN) — c'est justement
 * le geste que le ticket demande de rétablir.
 */
export const apiAffaireTasks = {
  list: (affaireId, archived = 'active') =>
    apiFetch(
      `/affaire-tasks?affaireId=${encodeURIComponent(affaireId)}&archived=${encodeURIComponent(archived)}`,
    ),
  get: (id) => apiFetch(`/affaire-tasks/${encodeURIComponent(id)}`),
  create: (affaireId, name) =>
    apiFetch('/affaire-tasks', {
      method: 'POST',
      body: JSON.stringify({ affaireId, name }),
    }),
  rename: (id, name) =>
    apiFetch(`/affaire-tasks/${encodeURIComponent(id)}`, {
      method: 'PATCH',
      body: JSON.stringify({ name }),
    }),
  archive: (id) => apiFetch(`/affaire-tasks/${encodeURIComponent(id)}`, { method: 'DELETE' }),
  restore: (id) =>
    apiFetch(`/affaire-tasks/${encodeURIComponent(id)}/restore`, { method: 'POST' }),
};

export const apiPhases = {
  list: (affaireId, archived = 'active') =>
    apiFetch(
      `/phases?affaireId=${encodeURIComponent(affaireId)}&archived=${encodeURIComponent(archived)}`,
    ),
  get: (id) => apiFetch(`/phases/${encodeURIComponent(id)}`),
};

/**
 * Système faceted filter (pattern Float — cf. analyse/float-filter-analysis.md).
 *
 * Modèle Float-style simple : `{ conditions: FilterCondition[] }` avec AND
 * implicite entre dimensions et OR implicite entre valeurs d'une condition.
 * Le filtre est sérialisé en JSON URI-encoded et passé en query param `filter`.
 *
 *  - dimensions(scope)            → métadonnée des dimensions du scope.
 *  - values(dimension, q, extra)  → autocomplete (entity_ref) ou liste statique.
 *  - encode(filter)               → string URI-encoded (ou '' si vide).
 */
/**
 * Ticket #37 — Vues de filtre sauvegardées (par scope).
 *
 *  - list(scope)   → GET /saved-views?scope=  (ses vues + les vues partagées)
 *  - get(id)       → GET /saved-views/:id     (lien partageable `?view=<id>`)
 *  - create({ scope, name, conditions, sort?, isDefault?, isShared? })
 *  - update(id, patch) → nom / conditions / tri / défaut / partage
 *  - remove(id)
 *
 * `conditions` est le tableau `[{ dimension, operator, values }]` produit par
 * `toFilterQuery(...)` (l'`id` local de l'UI est strippé avant persistance).
 * `sort` vaut `{ field, dir }` ou `null` (écran sans colonne triable).
 * L'écriture est réservée au propriétaire (404 sinon, côté serveur).
 */
export const apiSavedViews = {
  list: (scope) => apiFetch(`/saved-views?scope=${encodeURIComponent(scope)}`),
  get: (id) => apiFetch(`/saved-views/${encodeURIComponent(id)}`),
  create: (data) =>
    apiFetch('/saved-views', { method: 'POST', body: JSON.stringify(data) }),
  update: (id, patch) =>
    apiFetch(`/saved-views/${encodeURIComponent(id)}`, {
      method: 'PATCH',
      body: JSON.stringify(patch),
    }),
  remove: (id) =>
    apiFetch(`/saved-views/${encodeURIComponent(id)}`, { method: 'DELETE' }),
};

export const apiFilter = {
  dimensions: (scope) =>
    apiFetch(`/filter/dimensions?scope=${encodeURIComponent(scope)}`),
  values: (dimension, { q, limit, extra, ids } = {}) => {
    const params = new URLSearchParams();
    if (q) params.set('q', q);
    if (limit) params.set('limit', String(limit));
    if (Array.isArray(ids) && ids.length > 0) {
      params.set('ids', ids.map(String).join(','));
    }
    if (extra && typeof extra === 'object') {
      for (const [k, v] of Object.entries(extra)) {
        if (v !== undefined && v !== null && v !== '') params.set(k, String(v));
      }
    }
    return apiFetch(
      `/filter/values/${encodeURIComponent(dimension)}?${params.toString()}`,
    );
  },
  /**
   * Ticket #78 — recherche globale sur toutes les dimensions du scope.
   * Retour : { groups: [{ dimension, label, items: [{ id, label }] }] }.
   */
  search: (scope, q, { affaireId } = {}) => {
    const params = new URLSearchParams({ scope, q });
    if (affaireId) params.set('affaireId', affaireId);
    return apiFetch(`/filter/search?${params.toString()}`);
  },
  /**
   * Sérialise un filterQuery en string JSON brut pour `URLSearchParams.set`.
   * NE PAS faire `encodeURIComponent` ici : URLSearchParams encode déjà
   * automatiquement (un double encoding casse le `JSON.parse` côté backend).
   * Retourne '' si vide.
   */
  encode: (filterQuery) => {
    if (!filterQuery || !filterQuery.conditions || filterQuery.conditions.length === 0) {
      return '';
    }
    return JSON.stringify(filterQuery);
  },
};

/** Helper : ajoute le filterQuery dans une URLSearchParams (mutation in-place). */
function appendFilterParam(params, filterQuery) {
  const enc = apiFilter.encode(filterQuery);
  if (enc) params.set('filter', enc);
}

/**
 * Sprint 10 — Clients OAuth2 M2M (admin only côté UI).
 *
 *  - list(archived)         → GET /api/oauth/clients
 *  - get(id)                → GET /api/oauth/clients/:id
 *  - create({ name, scopes, expiresAt? }) → POST /api/oauth/clients
 *      Retour spécial : { client, clientSecret } — le clientSecret en clair
 *      n'est lisible QU'À LA CRÉATION (jamais re-récupérable ensuite).
 *  - update(id, { name?, scopes?, enabled?, expiresAt? }) → PATCH
 *  - rotateSecret(id)       → POST /:id/rotate-secret
 *      Retour : { client, clientSecret } — nouveau secret visible 1 fois.
 *  - archive(id)            → DELETE (soft-delete + enabled=false)
 *  - restore(id)            → POST /:id/restore
 *
 * La liste exhaustive des scopes V1 est servie par la constante OAUTH_SCOPES
 * côté front (importée depuis le module dédié — pas d'endpoint /scopes pour
 * éviter d'ajouter une route triviale).
 */
export const apiOAuthClients = {
  list: (archived = 'active') =>
    apiFetch(`/oauth/clients?archived=${encodeURIComponent(archived)}`),
  get: (id) => apiFetch(`/oauth/clients/${encodeURIComponent(id)}`),
  create: (data) =>
    apiFetch('/oauth/clients', { method: 'POST', body: JSON.stringify(data) }),
  update: (id, data) =>
    apiFetch(`/oauth/clients/${encodeURIComponent(id)}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    }),
  rotateSecret: (id) =>
    apiFetch(`/oauth/clients/${encodeURIComponent(id)}/rotate-secret`, {
      method: 'POST',
    }),
  archive: (id) =>
    apiFetch(`/oauth/clients/${encodeURIComponent(id)}`, { method: 'DELETE' }),
  restore: (id) =>
    apiFetch(`/oauth/clients/${encodeURIComponent(id)}/restore`, { method: 'POST' }),
};

/**
 * Sprint 10.3 — Webhooks sortants (admin only).
 *
 * Endpoints : CRUD standard + `rotateSecret` (nouveau secret HMAC visible 1 fois).
 * Deliveries : lecture seule pour debug (status, response, retry).
 */
export const apiWebhooks = {
  listEndpoints: (archived = 'active') =>
    apiFetch(`/webhooks/endpoints?archived=${encodeURIComponent(archived)}`),
  getEndpoint: (id) => apiFetch(`/webhooks/endpoints/${encodeURIComponent(id)}`),
  createEndpoint: (data) =>
    apiFetch('/webhooks/endpoints', { method: 'POST', body: JSON.stringify(data) }),
  updateEndpoint: (id, data) =>
    apiFetch(`/webhooks/endpoints/${encodeURIComponent(id)}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    }),
  rotateSecret: (id) =>
    apiFetch(`/webhooks/endpoints/${encodeURIComponent(id)}/rotate-secret`, {
      method: 'POST',
    }),
  archiveEndpoint: (id) =>
    apiFetch(`/webhooks/endpoints/${encodeURIComponent(id)}`, { method: 'DELETE' }),
  restoreEndpoint: (id) =>
    apiFetch(`/webhooks/endpoints/${encodeURIComponent(id)}/restore`, { method: 'POST' }),
  listDeliveries: ({ endpointId, status, limit = 50 } = {}) => {
    const params = new URLSearchParams();
    if (endpointId) params.set('endpointId', endpointId);
    if (status) params.set('status', status);
    if (limit) params.set('limit', String(limit));
    return apiFetch(`/webhooks/deliveries?${params.toString()}`);
  },
};

/**
 * Endpoint optimisé pour la vue Schedule (Sprint 2.A).
 * Retourne `{ persons, allocations, timeOffs }` pour la fenêtre [from, to].
 *
 * Sprint 8 — ajout `filter` (FilterQuery) propagé au backend.
 */
export const apiSchedule = {
  // Ticket #91 — `signal` : annulation d'un segment devenu inutile.
  // Ticket #77 — `scope` : 'all' (défaut) | 'team' (MANAGER / ADMIN).
  get: ({ from, to, personIds, withHolidays, filter, scope, signal } = {}) => {
    const params = new URLSearchParams();
    if (from) params.set('from', from);
    if (to) params.set('to', to);
    if (personIds && personIds.length > 0) {
      params.set('personIds', personIds.join(','));
    }
    if (withHolidays) params.set('withHolidays', '1');
    if (scope && scope !== 'all') params.set('scope', scope);
    appendFilterParam(params, filter);
    return apiFetch(`/schedule?${params.toString()}`, { signal });
  },
  /**
   * Sprint 17 — ouvre le flux SSE temps réel. Le serveur émet un event
   * `schedule.change` à chaque mutation d'allocation/congé ; l'appelant
   * re-fetch alors `/api/schedule`. Retourne `{ close }`.
   */
  stream: (handlers) => openEventStream('/schedule/stream', handlers),
};

/**
 * Export Gescom — pousse/supprime les saisies Synolog vers Gescom (Penta).
 * Base `/api/gescom-export`, ADMIN only côté backend.
 *  - preview({from,to,personIds?}) → POST /preview : aperçu des lignes
 *    exportables / bloquées + compteurs + état `configured`/`periodLocked`.
 *  - push(...) / delete(...)       → POST /push|/delete : lance un job batch
 *    côté backend (202 { jobId }) ; le suivi se fait via SSE.
 *  - job(jobId)                    → GET /jobs/:jobId : état complet du job
 *    (status, totals, items, lastError).
 *  - stream(jobId, handlers)       → SSE des events `gescom.progress` /
 *    `gescom.done` (helper `openEventStream`, Bearer, pas de ?token=).
 * `from`/`to` : "YYYY-MM-DD". `personIds` : array de cuid (absent = toutes).
 */
/** Périmètre d'un export Gescom : période + sélection / exclusion de personnes. */
function gescomScope({ from, to, personIds, excludedPersonIds, sessionId, lineKeys, forceKeys } = {}) {
  return {
    from,
    to,
    ...(personIds?.length ? { personIds } : {}),
    ...(excludedPersonIds?.length ? { excludedPersonIds } : {}),
    // Session : la période est celle de la session (imposée côté serveur).
    ...(sessionId ? { sessionId } : {}),
    ...(lineKeys?.length ? { lineKeys } : {}),
    // Remplacements choisis malgré un écart avec Gescom.
    ...(forceKeys?.length ? { forceKeys } : {}),
  };
}

export const apiGescomExport = {
  // `excludedPersonIds` (« Sauf les collaborateurs ») doit suivre : il n'était
  // pas transmis, l'exclusion restait sans effet côté serveur.
  preview: (scope = {}) =>
    apiFetch('/gescom-export/preview', { method: 'POST', body: JSON.stringify(gescomScope(scope)) }),
  push: (scope = {}) =>
    apiFetch('/gescom-export/push', { method: 'POST', body: JSON.stringify(gescomScope(scope)) }),
  delete: (scope = {}) =>
    apiFetch('/gescom-export/delete', { method: 'POST', body: JSON.stringify(gescomScope(scope)) }),
  /** Suggestions (code CH) + recherche dans le catalogue des projets Gescom. */
  affaireLinkInfo: (affaireId, { q, includeInactive, refresh } = {}) => {
    const qs = new URLSearchParams();
    if (q) qs.set('q', q);
    if (includeInactive) qs.set('includeInactive', '1');
    if (refresh) qs.set('refresh', '1');
    const suffix = qs.toString() ? `?${qs}` : '';
    return apiFetch(`/gescom-export/affaires/${encodeURIComponent(affaireId)}/gescom-project${suffix}`);
  },
  /**
   * Catalogue Gescom sans affaire existante : suggestions par code CH de
   * `name`, recherche `q`, résolution de `projectId` (fiche affaire).
   */
  catalog: ({ name, q, projectId, includeInactive, refresh } = {}) => {
    const qs = new URLSearchParams();
    if (name) qs.set('name', name);
    if (q) qs.set('q', q);
    if (projectId) qs.set('projectId', projectId);
    if (includeInactive) qs.set('includeInactive', '1');
    if (refresh) qs.set('refresh', '1');
    const suffix = qs.toString() ? `?${qs}` : '';
    return apiFetch(`/gescom-export/projects${suffix}`);
  },
  /** Lie (ou délie avec `null`) une affaire à un projet Gescom. `sessionId` : tracé dans son journal. */
  linkAffaire: (affaireId, gescomProjectId, sessionId) =>
    apiFetch(`/gescom-export/affaires/${encodeURIComponent(affaireId)}/gescom-project`, {
      method: 'PUT',
      body: JSON.stringify({ gescomProjectId, ...(sessionId ? { sessionId } : {}) }),
    }),
  // --- Sessions d'export (période figée + journal + suivi des lignes) ---
  sessions: () => apiFetch('/gescom-export/sessions'),
  createSession: ({ name, from, to, notes }) =>
    apiFetch('/gescom-export/sessions', { method: 'POST', body: JSON.stringify({ name, from, to, ...(notes ? { notes } : {}) }) }),
  session: (id) => apiFetch(`/gescom-export/sessions/${encodeURIComponent(id)}`),
  sessionStatus: (id) => apiFetch(`/gescom-export/sessions/${encodeURIComponent(id)}/status`),
  updateSession: (id, patch) =>
    apiFetch(`/gescom-export/sessions/${encodeURIComponent(id)}`, { method: 'PATCH', body: JSON.stringify(patch) }),
  resyncSession: (id) =>
    apiFetch(`/gescom-export/sessions/${encodeURIComponent(id)}/resync`, { method: 'POST', body: '{}' }),
  retrySessionErrors: (id) =>
    apiFetch(`/gescom-export/sessions/${encodeURIComponent(id)}/retry-errors`, { method: 'POST', body: '{}' }),
  job: (jobId) => apiFetch(`/gescom-export/jobs/${encodeURIComponent(jobId)}`),
  stream: (jobId, handlers) =>
    openEventStream(`/gescom-export/jobs/${encodeURIComponent(jobId)}/stream`, handlers),
};
